import { Button, Group, Modal, Text } from "@mantine/core";

interface ConfirmationModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  onConfirm: () => void;
  type: "close" | "update" | "return-to-storage" | "close-and-return" | "reset";
  isProcessing?: boolean;
}

function getConfig(type: ConfirmationModalProps["type"]) {
  switch (type) {
    case "close":
      return {
        title: "Close Platform",
        message:
          "Are you sure you want to close this platform? All progress will be lost.",
        buttonText: "Close",
        color: "red",
      };
    case "update":
      return {
        title: "Update Platform",
        message: "Are you sure you want to update this platform?",
        buttonText: "Confirm Update",
        color: "primary",
      };
    case "return-to-storage":
      return {
        title: "Return to Storage",
        message: "Are you sure you want to update and return this platform?",
        buttonText: "Update & Return",
        color: "primary",
      };
    case "close-and-return":
      return {
        title: "Close & Return Platform",
        message:
          "Are you sure you want to close and return this platform to storage without saving changes?",
        buttonText: "Close & Return",
        color: "red",
      };
    case "reset":
      return {
        title: "Reset Platform Status",
        message:
          "Are you sure you want to reset this platform's status? Any active operation will be cancelled and the platform set back to storage.",
        buttonText: "Reset Status",
        color: "red",
      };
    default:
      return {
        title: "Confirmation",
        message: "Are you sure you want to proceed?",
        buttonText: "Confirm",
        color: "primary",
      };
  }
}

function ConfirmationModal({
  isOpen,
  onRequestClose,
  onConfirm,
  type,
  isProcessing = false,
}: ConfirmationModalProps) {
  const { title, message, buttonText, color } = getConfig(type);

  return (
    <Modal
      opened={isOpen}
      onClose={onRequestClose}
      title={
        <Text fw={600} fz="lg" c="primary.6">
          {title}
        </Text>
      }
      centered
      radius="lg"
      size={480}
      zIndex={1200}
    >
      <Text c="dimmed" fz="sm" mb="xl">
        {message}
      </Text>
      <Group justify="flex-end" gap="sm">
        <Button
          variant="subtle"
          color="gray"
          onClick={onRequestClose}
          disabled={isProcessing}
        >
          Cancel
        </Button>
        <Button color={color} onClick={onConfirm} loading={isProcessing}>
          {buttonText}
        </Button>
      </Group>
    </Modal>
  );
}

export default ConfirmationModal;
